import { Component, Suspense, lazy, useMemo, type ComponentType, type ReactNode } from 'react';
import { Loading } from '../ui/index.tsx';

interface State {
  error: Error | null;
}

/**
 * Aísla un módulo: si falla al cargar o al renderizar, solo ese módulo muestra
 * el error; la navegación y los demás módulos siguen disponibles.
 */
export class ModuleBoundary extends Component<{ moduleId: string; title: string; children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    console.error(JSON.stringify({ module: this.props.moduleId, error: error.message }));
  }

  override render() {
    if (this.state.error) {
      const staleModule = /dynamically imported module|Failed to fetch.*module|Importing a module script|Loading chunk|module script failed/i.test(this.state.error.message);
      return (
        <div className="state-box state-error" role="alert">
          <h3>El módulo «{this.props.title}» no está disponible</h3>
          <p>Se produjo un error solo en este módulo; el resto de la aplicación sigue funcionando.</p>
          <p className="small mono">{this.state.error.message}</p>
          {staleModule ? <>
            <p>Puede haber una nueva versión de la aplicación o un fallo de conexión. Conserva cualquier cambio pendiente antes de recargar.</p>
            <button type="button" className="btn" onClick={() => window.location.reload()}>Recargar aplicación</button>
          </> : <button type="button" className="btn" onClick={() => this.setState({ error: null })}>Reintentar</button>}
        </div>
      );
    }
    return this.props.children;
  }
}

const cache = new Map<string, ComponentType>();

/** Carga perezosa del módulo dentro de su aislador. */
export function ModuleView({ id, title, load }: { id: string; title: string; load: () => Promise<{ default: ComponentType }> }) {
  const Lazy = useMemo(() => {
    const hit = cache.get(id);
    if (hit) return hit;
    const L = lazy(load);
    cache.set(id, L);
    return L;
  }, [id, load]);
  return (
    <ModuleBoundary moduleId={id} title={title}>
      <Suspense fallback={<Loading label={`Cargando ${title}…`} />}>
        <Lazy />
      </Suspense>
    </ModuleBoundary>
  );
}
